import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Search } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'

type MarkdownModule = () => Promise<string>

const docsModules = import.meta.glob('/docs/**/*.md', {
  query: '?raw',
  import: 'default',
}) as Record<string, MarkdownModule>

function normalizeDocPath(filePath: string): string {
  return filePath.replace('/docs/', '').replace(/\.md$/, '')
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[`*_~[\]()]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

function makeReadableTitle(doc: string): string {
  const leaf = doc.split('/').pop() ?? doc
  if (leaf === 'index') return 'Documentation Home'
  return leaf
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function docHref(doc: string): string {
  return doc === 'index' ? '/docs' : `/docs/${doc}`
}

/** Sidebar order aligned with docs/index.md */
const DOC_SECTIONS: { label: string; docs: string[] }[] = [
  {
    label: 'Overview',
    docs: ['index'],
  },
  {
    label: 'Core Documents',
    docs: ['project-overview', 'configuration', 'tech-stack', 'project-structure'],
  },
  {
    label: 'API Reference',
    docs: ['api-catalog'],
  },
  {
    label: 'Integration Guides',
    docs: [
      'pverify-user-guide',
      'stedi-eligibility-api-guide',
      'stedi-api-catalog',
      'integration-decision-matrix',
    ],
  },
  {
    label: 'Additional',
    docs: ['patient-intake-flow-design', 'PRODUCTION_AUTH_COOKIES'],
  },
]

function sortDocsCatalogOrder(allDocs: string[]): string[] {
  const knownOrder = DOC_SECTIONS.flatMap((section) => section.docs)
  const rank = new Map(knownOrder.map((doc, index) => [doc, index]))
  return [...allDocs].sort((a, b) => {
    const rankA = rank.get(a) ?? Number.MAX_SAFE_INTEGER
    const rankB = rank.get(b) ?? Number.MAX_SAFE_INTEGER
    if (rankA !== rankB) return rankA - rankB
    return a.localeCompare(b)
  })
}

function groupDocsForSidebar(allDocs: string[]): { label: string; docs: string[] }[] {
  const available = new Set(allDocs)
  const placed = new Set<string>()
  const sections: { label: string; docs: string[] }[] = []

  for (const section of DOC_SECTIONS) {
    const docs = section.docs.filter((doc) => available.has(doc))
    docs.forEach((doc) => placed.add(doc))
    if (docs.length > 0) {
      sections.push({ label: section.label, docs })
    }
  }

  const remaining = sortDocsCatalogOrder(allDocs.filter((doc) => !placed.has(doc)))
  if (remaining.length > 0) {
    const additional = sections.find((s) => s.label === 'Additional')
    if (additional) {
      additional.docs.push(...remaining)
    } else {
      sections.push({ label: 'Additional', docs: remaining })
    }
  }

  return sections
}

/** Map markdown file links (e.g. `./api-catalog.md`) to in-app `/docs/...` routes. */
function resolveInternalDocRoute(href: string): string | null {
  const trimmed = href.trim()
  if (!trimmed) return null
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return null

  if (trimmed.startsWith('#')) {
    return trimmed
  }

  const hashIndex = trimmed.indexOf('#')
  const pathPart = (hashIndex >= 0 ? trimmed.slice(0, hashIndex) : trimmed).trim()
  const hash = hashIndex >= 0 ? trimmed.slice(hashIndex + 1) : ''

  if (!pathPart) {
    return hash ? `#${hash}` : null
  }

  if (pathPart.startsWith('/docs')) {
    return hash ? `${pathPart}#${hash}` : pathPart
  }

  const mdMatch = pathPart.match(/^(?:\.\/|\.\.\/)*(?:docs\/)?([^?#]+\.md)$/i)
  if (mdMatch) {
    const slug = mdMatch[1].replace(/\.md$/i, '')
    const route = slug === 'index' ? '/docs' : `/docs/${slug}`
    return hash ? `${route}#${hash}` : route
  }

  return null
}

function flattenText(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }
  if (!children) {
    return ''
  }
  if (Array.isArray(children)) {
    return children.map((child) => flattenText(child)).join('')
  }
  if (typeof children === 'object' && 'props' in children) {
    return flattenText((children as { props?: { children?: ReactNode } }).props?.children ?? '')
  }
  return ''
}

export default function MarkdownDocs() {
  const { theme } = useTheme()
  const params = useParams()
  const slug = params['*'] ?? ''
  const cleanSlug = slug.replace(/^\/+/, '').replace(/\/+$/, '')
  const selectedDoc = cleanSlug || 'index'

  const docs = useMemo(
    () => sortDocsCatalogOrder(Object.keys(docsModules).map((path) => normalizeDocPath(path))),
    [],
  )

  const docSections = useMemo(() => groupDocsForSidebar(docs), [docs])

  const [content, setContent] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchText, setSearchText] = useState('')

  const headingLinks = useMemo(() => {
    const lines = content.split('\n')
    const matches = lines
      .map((line) => line.match(/^(#{1,4})\s+(.*)$/))
      .filter((match): match is RegExpMatchArray => Boolean(match))
      .map((match) => ({
        level: match[1].length,
        title: match[2].trim(),
      }))

    const used = new Map<string, number>()
    return matches.map((item) => {
      const rawId = slugify(item.title) || 'section'
      const count = used.get(rawId) ?? 0
      used.set(rawId, count + 1)
      return {
        ...item,
        id: count === 0 ? rawId : `${rawId}-${count}`,
      }
    })
  }, [content])

  const filteredDocs = useMemo(() => {
    const query = searchText.trim().toLowerCase()
    if (!query) {
      return docs
    }
    return docs.filter(
      (doc) =>
        doc.toLowerCase().includes(query) ||
        makeReadableTitle(doc).toLowerCase().includes(query),
    )
  }, [docs, searchText])

  const filteredSections = useMemo(() => {
    if (!searchText.trim()) {
      return docSections
    }
    const matchSet = new Set(filteredDocs)
    return docSections
      .map((section) => ({
        ...section,
        docs: section.docs.filter((doc) => matchSet.has(doc)),
      }))
      .filter((section) => section.docs.length > 0)
  }, [docSections, filteredDocs, searchText])

  useEffect(() => {
    const key = `/docs/${selectedDoc}.md`
    const loader = docsModules[key]
    if (!loader) {
      setIsLoading(false)
      setError(`Document not found: ${selectedDoc}.md`)
      setContent('')
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError('')
    loader()
      .then((markdown) => {
        if (!cancelled) {
          setContent(markdown)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(`Unable to load document: ${selectedDoc}.md`)
          setContent('')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [selectedDoc])

  return (
    <div className="min-h-screen text-slate-800" style={{ backgroundColor: theme.colors.background }}>
      <div className="mx-auto grid min-h-screen w-full max-w-[1680px] lg:grid-cols-[260px,1fr,220px]">
        <aside
          className="border-r bg-white"
          style={{ borderColor: theme.colors.sidebarBorder, backgroundColor: theme.colors.sidebarBackground }}
        >
          <div className="border-b px-5 py-4" style={{ borderColor: theme.colors.sidebarBorder }}>
            <p className="text-[30px] font-bold leading-none tracking-tight" style={{ color: theme.colors.primary }}>
              Health Lynk
            </p>
            <p className="mt-1 text-[11px]" style={{ color: theme.colors.textSecondary }}>
              Web Application Documentation
            </p>
            <button
              className="mt-3 w-full rounded-md px-3 py-2 text-xs font-medium"
              style={{
                backgroundColor: theme.colors.primaryLight,
                color: theme.colors.primary,
              }}
              type="button"
            >
              Refresh
            </button>
          </div>

          <div className="p-4">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                size={16}
                style={{ color: theme.colors.textSecondary }}
              />
              <input
                className="w-full rounded-md border bg-white py-2 pl-9 pr-3 text-sm outline-none"
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Quick search..."
                style={{
                  borderColor: theme.colors.border,
                  color: theme.colors.textPrimary,
                }}
                value={searchText}
              />
            </div>

            <div className="mt-4 space-y-4">
              {filteredSections.length === 0 ? (
                <p className="text-[13px]" style={{ color: theme.colors.textSecondary }}>
                  No documents found.
                </p>
              ) : (
                filteredSections.map((section) => (
                  <div key={section.label}>
                    <p
                      className="text-[11px] font-semibold uppercase tracking-widest"
                      style={{ color: theme.colors.textSecondary }}
                    >
                      {section.label}
                    </p>
                    <ul className="mt-1.5 space-y-0.5">
                      {section.docs.map((doc) => (
                        <li key={doc}>
                          <Link
                            className={`block rounded-md px-2.5 py-2 text-[13px] transition ${
                              doc === selectedDoc
                                ? 'font-semibold'
                                : 'font-medium hover:bg-slate-50'
                            }`}
                            style={
                              doc === selectedDoc
                                ? {
                                    backgroundColor: theme.colors.sidebarActiveBackground,
                                    color: theme.colors.sidebarActiveText,
                                  }
                                : { color: theme.colors.sidebarText }
                            }
                            to={docHref(doc)}
                          >
                            {makeReadableTitle(doc)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>

        <main className="p-4 md:p-5">
          <div
            className="rounded-xl border bg-white shadow-sm"
            style={{ borderColor: theme.colors.cardBorder, backgroundColor: theme.colors.cardBackground }}
          >
            <div
              className="flex flex-col gap-4 border-b px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6 sm:py-5"
              style={{ borderColor: theme.colors.border }}
            >
              <div className="min-w-0">
                <p
                  className="text-[11px] font-semibold uppercase tracking-widest"
                  style={{ color: theme.colors.primary }}
                >
                  Documentation
                </p>
                <h1
                  className="mt-1.5 text-2xl font-bold leading-tight tracking-tight sm:text-[1.75rem]"
                  style={{ color: theme.colors.textPrimary }}
                >
                  {makeReadableTitle(selectedDoc)}
                </h1>
                {selectedDoc === 'configuration' && (
                  <p className="mt-2 text-[15px] leading-relaxed" style={{ color: theme.colors.textSecondary }}>
                    This project uses environment variables and standard frontend build configuration.
                  </p>
                )}
              </div>
              <div className="flex gap-2 text-sm">
                <button
                  className="rounded-md border bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                  style={{ borderColor: theme.colors.border, color: theme.colors.textSecondary }}
                  type="button"
                >
                  Copy
                </button>
                <button
                  className="rounded-md border bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                  style={{ borderColor: theme.colors.border, color: theme.colors.textSecondary }}
                  type="button"
                >
                  Export PDF
                </button>
              </div>
            </div>

            <section className="px-5 py-6 sm:px-8 sm:py-8">
              {isLoading ? (
                <p className="text-[15px]" style={{ color: theme.colors.textSecondary }}>
                  Loading markdown...
                </p>
              ) : error ? (
                <p className="text-[15px] font-medium text-rose-600">{error}</p>
              ) : (
                <article className="markdown-doc-content">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => {
                        const text = flattenText(children)
                        return <h1 id={slugify(text)}>{children}</h1>
                      },
                      h2: ({ children }) => {
                        const text = flattenText(children)
                        return <h2 id={slugify(text)}>{children}</h2>
                      },
                      h3: ({ children }) => {
                        const text = flattenText(children)
                        return <h3 id={slugify(text)}>{children}</h3>
                      },
                      h4: ({ children }) => {
                        const text = flattenText(children)
                        return <h4 id={slugify(text)}>{children}</h4>
                      },
                      a: ({ href, children }) => {
                        if (!href) {
                          return <span>{children}</span>
                        }
                        const docRoute = resolveInternalDocRoute(href)
                        if (docRoute?.startsWith('#')) {
                          return <a href={docRoute}>{children}</a>
                        }
                        if (docRoute) {
                          return <Link to={docRoute}>{children}</Link>
                        }
                        return (
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        )
                      },
                    }}
                    remarkPlugins={[remarkGfm]}
                  >
                    {content}
                  </ReactMarkdown>
                </article>
              )}
            </section>
          </div>
        </main>

        <aside
          className="hidden border-l bg-white p-5 lg:block"
          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.cardBackground }}
        >
          <p
            className="text-[11px] font-semibold uppercase tracking-widest"
            style={{ color: theme.colors.textSecondary }}
          >
            On this page
          </p>
          <ul className="mt-4 space-y-0.5">
            {headingLinks.length === 0 ? (
              <li className="text-[13px]" style={{ color: theme.colors.textSecondary }}>
                No section headings.
              </li>
            ) : (
              headingLinks.map((heading) => (
                <li key={heading.id}>
                  <a
                    className={`block rounded-md border-l-2 border-transparent py-1.5 pr-2 transition hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-light)] ${
                      heading.level === 1
                        ? 'pl-2 text-[13px] font-semibold'
                        : heading.level === 2
                          ? 'pl-3 text-[13px] font-medium'
                          : heading.level === 3
                            ? 'pl-5 text-[12px]'
                            : 'pl-7 text-[12px]'
                    }`}
                    style={{ color: theme.colors.textSecondary }}
                    href={`#${heading.id}`}
                  >
                    {heading.title}
                  </a>
                </li>
              ))
            )}
          </ul>
        </aside>
      </div>
    </div>
  )
}
